import moment from 'moment';
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select from 'react-select';
import { IntervalConfig, IntervalStrategy } from '../../../../../pages/MeetingPage/MeetingForm/types';
import { buildStyleForSelectInput } from '../../../../../../utils/form/style.util';
import { IStep2Props } from './types';
import { generateTimeOptions } from '../../../../../../utils/form/startTimeOptions.util';

const repeatOptions = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

const intervalOptions = [
  // { value: 'doNotRepeat', label: 'Do not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: `Weekly on XXXXX` },
  { value: 'monthly', label: `Monthly on the XXXXX` },
  { value: 'annually', label: `Annually on XXXXX` },
  { value: 'custom', label: 'Custom...' },
];

const intervalStrategy: IntervalStrategy = {
  daily: { repeatType: { value: 'days', label: 'Days' }, repeatEvery: 1 },
  weekly: { repeatType: { value: 'weeks', label: 'Weeks' }, repeatEvery: 1 },
  monthly: { repeatType: { value: 'months', label: 'Months' }, repeatEvery: 1 },
  annually: { repeatType: { value: 'years', label: 'Years' }, repeatEvery: 1 },
};

const Step2: FunctionComponent<IStep2Props> = ({ onNext, data }) => {
  const [isCustomInterval, setIsCustomInterval] = useState(false);
  const [formData, setFormData] = useState({
    title: data.title ?? '',
    sharepointUrl: data.sharepointUrl ?? '',
    repeatEvery: data.repeatEvery ?? 0,
    repeatType: data.repeatType ?? '',
    startDate: data.startDate ? new Date(data.startDate) : new Date(),
    startTime: data.startTime ?? '',
    interval: data.interval ?? '',
  });

  const [formError, setFormError] = useState({
    title: false,
    sharepointUrl: false,
    startDate: false,
    startTime: false,
    repeatType: false,
    repeatEvery: false,
  });

  const handleChange = (name: string, value: any) => {
    if (value) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const currentDate = useMemo(() => {
    return {
      dayOfWeek: moment(formData.startDate).format('dddd'),
      dayOfMonth: moment(formData.startDate).date(),
      monthAndDay: moment(formData.startDate).format('MMMM Do'),
    };
  }, [formData.startDate]);

  const handleGetOptionLabel = (option?: { value: string; label: string }) => {
    switch (option?.value) {
      case 'weekly':
        return `Weekly on ${currentDate.dayOfWeek}`;
      case 'monthly':
        return `Monthly on the ${currentDate.dayOfMonth}`;
      case 'annually':
        return `Annually on ${currentDate.monthAndDay}`;
      default:
        return option?.label ?? '';
    }
  };

  const calculateRepeatBasedOnInverval = useCallback(
    (interval) => {
      if (interval) {
        setIsCustomInterval(interval === 'custom');
        if (interval === 'custom') {
          setFormData({ ...formData, repeatType: '', repeatEvery: 0 });
        } else {
          if (interval === 'doNotRepeat') {
            setFormData({ ...formData, repeatType: '', repeatEvery: 0 });
          } else {
            const { repeatEvery, repeatType }: IntervalConfig = intervalStrategy[interval];
            setFormData({ ...formData, repeatType: repeatType.value, repeatEvery });
          }
        }
      } else {
        // default is Weekly
        setFormData({ ...formData });
        setFormData({ ...formData, repeatEvery: 1, repeatType: 'weeks' });
      }
    },
    [setFormData, formData],
  );

  const handleIntervalChange = (e: any) => {
    calculateRepeatBasedOnInverval(e.value);
    handleChange('interval', e.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.title ||
      !formData.sharepointUrl ||
      !formData.repeatEvery ||
      !formData.repeatType ||
      !formData.startDate ||
      !formData.startTime
    ) {
      setFormError({
        title: !formData.title,
        sharepointUrl: !formData.sharepointUrl,
        repeatEvery: !formData.repeatEvery,
        repeatType: !formData.repeatType,
        startDate: !formData.startDate,
        startTime: !formData.startTime,
      });
      return;
    }

    onNext(formData);
  };

  return (
    <form id="meeting-templates-form" className="topic-form" onSubmit={handleSubmit} method="post">
      <div className="row">
        <div className="col-12" style={{ marginTop: '20px' }}>
          <div className="field-container">
            <label htmlFor="title">Meeting Name *</label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              placeholder="Enter Title"
              onChange={(e) => handleChange('title', e.target.value)}
            />
            {formError.title && !formData.title && (
              <div className="flex mt-1">
                <label htmlFor="title" style={{ color: 'red' }}>
                  Meeting Name * - This field is required
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="field-container">
            <label htmlFor="sharepointUrl">Meeting URL (ie - Zoom, Teams, Meet, etc.)</label>
            <input
              id="sharepointUrl"
              name="sharepointUrl"
              type="text"
              value={formData.sharepointUrl}
              placeholder="Enter Meeting URL"
              onChange={(e) => handleChange('sharepointUrl', e.target.value)}
            />
            {formError.sharepointUrl && !formData.sharepointUrl && (
              <div className="flex mt-1">
                <label htmlFor="title" style={{ color: 'red' }}>
                  Meeting URL * - This field is required
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="field-container">
            <label htmlFor="startDate">Start Date *</label>
            <ReactDatePicker
              id="startDate"
              name="startDate"
              selected={formData.startDate}
              onChange={(value) => handleChange('startDate', value)}
              placeholderText="Enter Start Date"
            />
          </div>
        </div>
        <div className="col-6">
          <div className="field-container">
            <label htmlFor="startTime">Start Time *</label>
            <Select<{ label: string; value: string }, false>
              id="startTime"
              name="startTime"
              value={timeOptions.find((o) => o.value === formData.startTime)}
              options={timeOptions}
              onChange={(e: { label: string; value: string } | null) => handleChange('startTime', e?.value)}
              className="custom-select-new"
              styles={buildStyleForSelectInput(1)}
            />
            {formError.startTime && !formData.startTime && (
              <div className="flex mt-1">
                <label htmlFor="startTime" style={{ color: 'red' }}>
                  Start time * - This field is required
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="field-container">
            <div className="field-container">
              <label htmlFor="interval">Repeat</label>
              <Select<{ label: string; value: string }, false>
                id="interval"
                name="interval"
                value={intervalOptions.find((o) => o.value === formData.interval)}
                getOptionLabel={handleGetOptionLabel}
                options={intervalOptions}
                onChange={handleIntervalChange}
                className="custom-select-new"
                styles={buildStyleForSelectInput(1)}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row" style={isCustomInterval ? {} : { display: 'none' }}>
        <div className="col-12">
          <div className="field-container">
            <label htmlFor="interval">Repeat Every</label>
          </div>
        </div>
      </div>
      <div className="row" style={isCustomInterval ? {} : { display: 'none' }}>
        <div className="col-6">
          <div className="field-container">
            <input
              type="text"
              id="repeatEvery"
              name="repeatEvery"
              value={formData.repeatEvery || ''}
              onChange={(e) => setFormData({ ...formData, repeatEvery: parseInt(e.target.value) })}
              style={{ height: 54 }}
            />
            {formError.repeatEvery && !formData.repeatEvery && (
              <div className="flex mt-1">
                <label htmlFor="repeatEvery" style={{ color: 'red' }}>
                  Repeat * - This field is required
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="col-6">
          <div className="field-container">
            <Select<{ label: string; value: string }, false>
              id="repeatType"
              name="repeatType"
              options={repeatOptions}
              onChange={(e: { label: string; value: string } | null) => handleChange('repeatType', e?.value)}
              className="custom-select-new"
              styles={buildStyleForSelectInput(1)}
            />
            {formError.repeatType && !formData.repeatType && (
              <div className="flex mt-1">
                <label htmlFor="repeatType" style={{ color: 'red' }}>
                  Repeat * - This field is required
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};
export default Step2;
